import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, CreditCard, ShieldCheck, ArrowRight, Smartphone, Mail, Store, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';

const CheckoutPage = () => {
    const [searchParams] = useSearchParams();
    const tenantId = searchParams.get('tenantId');
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tenantData, setTenantData] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!tenantId) {
            navigate('/');
            return;
        }

        const fetchTenantInfo = async () => {
            try {
                // Buscamos os dados da assinatura do lojista
                const res = await axios.get(`https://zapshop.app/api/admin/store/${tenantId}/subscription`);
                setTenantData(res.data);
            } catch (err: any) {
                console.error('Erro ao buscar dados do checkout:', err);
                setError('Não conseguimos localizar seu cadastro. Verifique o link ou entre em contato com o suporte.');
            } finally {
                setLoading(false);
            }
        };

        fetchTenantInfo();
    }, [tenantId, navigate]);

    const handlePayment = async () => {
        setSubmitting(true);
        try {
            // Se o tenant já tiver uma URL de checkout, usamos ela. 
            // Caso contrário, tentamos gerar uma nova (o backend já faz isso no create-tenant, 
            // mas podemos adicionar uma lógica de retry aqui se necessário no futuro).

            // Atualmente, confiamos que o link de pagamento está no tenant_data ou pedimos suporte
            if (tenantData?.checkout_url) {
                window.location.href = tenantData.checkout_url;
            } else {
                // Fallback: Tentamos gerar o link via API interna se não veio no primeiro passo
                const res = await axios.get(`https://zapshop.app/api/store/${tenantId}`);
                const store = res.data;

                // Redirecionamento dinâmico baseado no plano
                const planId = tenantData?.plan_type === 'PRO' ? 'ac959db5c4a5453295e5d3d986e81802' :
                    tenantData?.plan_type === 'INTERMEDIÁRIO' ? 'd924bdd20e404b22aaee2557e5604aa5' :
                        'ba5aae8244194c4b91248606cfc9825d';

                // Usamos a URL pública de checkout do MP como fallback seguro
                const mpCheckoutUrl = `https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=${planId}&payer_email=${store.email}`;
                window.location.href = mpCheckoutUrl;
            }
        } catch (err) {
            showToast('Erro ao redirecionar para o pagamento. Fale com nosso suporte no WhatsApp.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-surface-50 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-brand-500 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-surface-50 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mb-6">
                    <AlertCircle size={40} />
                </div>
                <h1 className="text-2xl font-black text-surface-900 mb-2">Ops! Algo deu errado.</h1>
                <p className="text-surface-500 max-w-sm mb-8">{error}</p>
                <button onClick={() => navigate('/')} className="bg-surface-900 text-white px-8 py-4 rounded-2xl font-bold transition-all hover:scale-105 active:scale-95">
                    Voltar para o Início
                </button>
            </div>
        );
    }

    const planPrices: any = {
        'BÁSICO': '97',
        'INTERMEDIÁRIO': '197',
        'PRO': '497'
    };

    return (
        <div className="min-h-screen bg-surface-50 font-sans p-6 md:p-12 flex flex-col items-center relative overflow-hidden">
            {/* Decor Background */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/5 rounded-full blur-[120px] -mr-64 -mt-64"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[120px] -ml-64 -mb-64"></div>

            <div className="w-full max-w-4xl relative z-10 grid md:grid-cols-2 gap-8 items-center">

                {/* Lado Esquerdo: Info */}
                <div className="space-y-8 animate-fade-in-up">
                    <img src="/logozapshopsaas.png" alt="ZapShop" className="h-12" />

                    <div className="space-y-4">
                        <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border border-emerald-100">
                            <CheckCircle2 size={14} /> Conta Criada com Sucesso
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-surface-900 leading-tight tracking-tight">
                            Só falta um passo para sua <span className="text-brand-600 italic">loja decolar.</span>
                        </h1>
                        <p className="text-surface-500 text-lg font-medium leading-relaxed">
                            Ative sua assinatura agora para liberar todas as ferramentas automáticas da ZapShop e começar a vender pelo WhatsApp hoje mesmo.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white p-6 rounded-3xl border border-surface-100 shadow-sm">
                            <Smartphone className="text-brand-500 mb-3" size={24} />
                            <h4 className="text-xs font-black text-surface-400 uppercase tracking-widest mb-1">Pagamento</h4>
                            <p className="text-sm font-bold text-surface-900">Mercado Pago</p>
                        </div>
                        <div className="bg-white p-6 rounded-3xl border border-surface-100 shadow-sm">
                            <ShieldCheck className="text-emerald-500 mb-3" size={24} />
                            <h4 className="text-xs font-black text-surface-400 uppercase tracking-widest mb-1">Segurança</h4>
                            <p className="text-sm font-bold text-surface-900">100% Blindado</p>
                        </div>
                    </div>
                </div>

                {/* Lado Direito: Card de Pagamento */}
                <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-brand-900/10 border border-surface-100 p-8 md:p-10 animate-fade-in">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-brand-50 text-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-brand-100">
                            <CreditCard size={32} />
                        </div>
                        <h2 className="text-2xl font-black text-surface-900">Assinatura Mensal</h2>
                        <p className="text-surface-400 font-medium">Plano {tenantData?.plan_type || 'BÁSICO'}</p>
                    </div>

                    <div className="bg-surface-50 rounded-3xl p-6 mb-8 space-y-4">
                        <div className="flex justify-between items-center pb-4 border-b border-surface-200">
                            <span className="text-surface-500 font-bold">Valor Mensal</span>
                            <div className="text-right">
                                <span className="text-2xl font-black text-surface-900">R$ {planPrices[tenantData?.plan_type || 'BÁSICO']},00</span>
                                <p className="text-[10px] font-black uppercase text-surface-400">será cobrado recorrentemente todo mês</p>
                            </div>
                        </div>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-2 text-sm text-surface-600 font-medium">
                                <CheckCircle2 size={16} className="text-emerald-500" /> Vendas Ilimitadas no WhatsApp
                            </li>
                            <li className="flex items-center gap-2 text-sm text-surface-600 font-medium">
                                <CheckCircle2 size={16} className="text-emerald-500" /> IA de Atendimento 24h
                            </li>
                            <li className="flex items-center gap-2 text-sm text-surface-600 font-medium">
                                <CheckCircle2 size={16} className="text-emerald-500" /> Gestão de Pedidos & CRM
                            </li>
                        </ul>
                    </div>

                    <button
                        onClick={handlePayment}
                        disabled={submitting}
                        className="w-full bg-brand-600 hover:bg-brand-700 text-white py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-brand-600/30 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70"
                    >
                        {submitting ? 'Processando...' : (
                            <>
                                Ativar Agora via Mercado Pago <ArrowRight size={18} />
                            </>
                        )}
                    </button>

                    <div className="mt-8 flex items-center justify-center gap-6 opacity-30 grayscale saturate-0">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-4" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_logo%2C_revised_2016.svg" alt="Stripe" className="h-4" />
                        <img src="https://logospng.org/download/mercado-pago/logo-mercado-pago-2048.png" alt="Mercado Pago" className="h-4" />
                    </div>

                    <p className="mt-8 text-center text-[10px] text-surface-400 font-bold uppercase tracking-widest leading-loose">
                        Ao clicar em ativar, você será redirecionado para o ambiente seguro do Mercado Pago para concluir sua assinatura mensal.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
